import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.ts";
import { canClearUp, emptyAnswer, withDeadline } from "../lib/requests.ts";

// Admins add field observers here. Adding someone writes an invite and nothing
// else: no email goes out from this screen. The person signs in with that
// address and the database gives them the role at that moment, or immediately
// if they had already tried and been turned away.
//
// The owner sees two things nobody else does: adding someone as an admin, and
// taking access away or giving it back. Hiding those from everyone else is a
// courtesy, not the control. The database refuses an admin invite from anyone
// but the owner, and refuses `set_member_access` outright, whatever this screen
// chooses to render.

interface Member {
  id: string;
  email: string | null;
  /** Asked once at first sign-in; null until they answer. */
  display_name: string | null;
  role: string;
  created_at: number;
  revoked_at: number | null;
  is_owner: boolean;
}
interface Invite { email: string; role: string; created_at: number }

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
// `collector` is the stored value; "Field observer" is what people read.
const label = (role: string, owner = false) =>
  owner ? "Owner" : role === "admin" ? "Admin" : "Field observer";

export default function Team({ userId, isOwner }: { userId: string; isOwner: boolean }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"collector" | "admin">("collector");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A failed load of the list, kept apart from the form's own errors. It used to
  // land in the form's message and leave "Loading..." under it for good, with
  // nothing to press (verifier finding on PR #9).
  const [loadErr, setLoadErr] = useState<string | null>(null);

  async function load() {
    setLoadErr(null);
    const failed = (e: { message: string; code?: string }, status?: number) =>
      setLoadErr(canClearUp(e, status) ? "Could not load the team. Check your signal and try again." : e.message);
    try {
      // Cut off at 15 s, the sign-in renewal included, so a dead zone ends in
      // Try again rather than a list that never arrives.
      const [m, i] = await Promise.all([
        withDeadline((signal) => supabase.from("profiles")
          .select("id,email,display_name,role,created_at,revoked_at,is_owner").order("created_at").abortSignal(signal)),
        withDeadline((signal) => supabase.from("team_invites")
          .select("email,role,created_at").order("created_at", { ascending: false }).abortSignal(signal)),
      ]);
      if (m.error) { failed(m.error, m.status); return; }
      if (i.error) { failed(i.error, i.status); return; }
      // A success with no body is not an empty team.
      if (m.data == null || i.data == null) { failed(emptyAnswer); return; }
      setMembers(m.data.map((r) => ({ ...r, created_at: Number(r.created_at) })));
      setInvites(i.data.map((r) => ({ ...r, created_at: Number(r.created_at) })));
    } catch (e) {
      failed({ message: e instanceof Error ? e.message : String(e) });
    }
  }
  useEffect(() => { void load(); }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    setErr(null); setNotice(null);
    const wanted = isOwner ? role : "collector";

    // Someone already on the team is not a new invite, and used to be answered
    // with "Added" while nothing changed. Three cases, not one: access removed,
    // nothing to do, or the reason the owner exists, which is promoting a field
    // observer who has already signed in.
    const existing = (members ?? []).find((m) => (m.email ?? "").toLowerCase() === address);
    let promoting = false;
    if (existing) {
      if (existing.revoked_at) {
        setErr(`${address} is on the team with access removed. ${isOwner ? "Give their access back instead of adding them again." : "Only the owner can give it back."}`);
        return;
      }
      if (existing.is_owner || existing.role === "admin" || wanted !== "admin") {
        setErr(`${address} is already on the team as ${label(existing.role, existing.is_owner).toLowerCase()}.`);
        return;
      }
      promoting = true;
    }

    setBusy(true);
    // A promotion goes through the same invite the database already acts on, so
    // the app still never writes a role: the trigger on the invite does. The old
    // field observer invite has to go first, because the address is the key and
    // invites have no update policy.
    //
    // Ask the database who is asking before deleting anything. This flag also
    // comes from a cache for weak signal, and an admin may delete a field
    // observer invite, so a stale or tampered `true` would otherwise destroy an
    // invite on the way to an insert the policy then refuses.
    if (promoting) {
      const me = await supabase.from("profiles").select("is_owner").eq("id", userId).maybeSingle();
      if (me.error) { setBusy(false); setErr(me.error.message); return; }
      if (me.data?.is_owner !== true) {
        setBusy(false);
        setErr("Only the owner can add an admin.");
        return;
      }
      const gone = await supabase.from("team_invites").delete().eq("email", address).select("email");
      if (gone.error) { setBusy(false); setErr(gone.error.message); return; }
    }
    const { error } = await supabase.from("team_invites").insert({
      email: address, role: wanted, invited_by: userId, created_at: Date.now(),
    });
    setBusy(false);
    if (error) {
      // 23505 is a unique violation. On a promotion it means the old field
      // observer invite is still there, because a refused delete comes back as
      // zero rows rather than an error. 42501 is the policy refusing, which
      // arrives as raw Postgres text that is no use to a reader.
      const policyRefusal = error.code === "42501" && /row.level security/i.test(error.message);
      setErr(error.code === "23505"
          ? promoting
            ? `Could not promote ${address}: their field observer invite is still there.`
            : `${address} is already on the list.`
        : policyRefusal ? "Only the owner can add an admin."
        : error.message);
      return;
    }
    setEmail("");
    setNotice(promoting
      ? `${address} is now an admin.`
      : `Added ${address} as ${wanted === "admin" ? "an admin" : "a field observer"}. Send them the link below and ask them to sign in with that email.`);
    void load();
  }

  // Withdraws an invite nobody has used yet. A mistyped address used to stay
  // redeemable forever, so whoever held that mailbox could join months later.
  async function remove(address: string) {
    if (!confirm(`Remove ${address}? They will not be able to sign in.`)) return;
    setErr(null); setNotice(null);
    // Row level security turns a refused delete into zero rows, not an error,
    // so ask for the deleted rows back and check one came.
    const { data, error } = await supabase.from("team_invites").delete().eq("email", address).select("email");
    if (error) { setErr(error.message); return; }
    if (!data || data.length === 0) { setErr(`Could not remove ${address}.`); return; }
    setNotice(`Removed ${address}.`);
    void load();
  }

  // Taking access away, and giving it back. Through the database function, not
  // a write to the profile: the function is the only path that can set the
  // revoked stamp, and it refuses everyone but the owner. Their recorded
  // sessions stay, because the rows point at that profile.
  async function setAccess(m: Member, revoke: boolean) {
    const who = m.email ?? "this person";
    const question = revoke
      ? `Remove access for ${who}? Their recorded sessions stay, but they cannot sign in.`
      : `Give ${who} access again?`;
    if (!confirm(question)) return;
    setErr(null); setNotice(null); setBusy(true);
    // The function hands back the row it wrote. Reporting that, rather than what
    // this screen believed a moment ago, is what keeps two open tabs honest.
    const { data, error } = await supabase.rpc("set_member_access", { p_target: m.id, p_revoke: revoke });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    // The function returns one row; PostgREST may hand it over bare or in a
    // one-element array, and reading the wrong shape would report a revoke as a
    // restore, which is the mistake this whole block exists to avoid.
    const row = (Array.isArray(data) ? data[0] : data) as Member | undefined;
    const stamp = row?.revoked_at ?? null;
    setNotice(stamp === null ? `${who} has access again.` : `Removed access for ${who}.`);
    void load();
  }

  const link = window.location.origin;
  const joined = new Set((members ?? []).map((m) => (m.email ?? "").toLowerCase()));
  const pending = invites.filter((i) => !joined.has(i.email));

  return (
    <div className="pad">
      <h2>Team</h2>
      <p className="muted">Only people added here, or made admins, can see anything.</p>

      <form className="card" onSubmit={add}>
        <h3>{isOwner ? "Add someone" : "Add a field observer"}</h3>
        <label htmlFor="invite">Their email</label>
        <input id="invite" type="email" required inputMode="email" autoComplete="off"
               value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        {isOwner && (
          <fieldset className="choice">
            <legend>Add them as</legend>
            <label htmlFor="as-collector">
              <input id="as-collector" type="radio" name="invite-role" value="collector"
                     checked={role === "collector"} onChange={() => setRole("collector")} />
              Field observer
            </label>
            <label htmlFor="as-admin">
              <input id="as-admin" type="radio" name="invite-role" value="admin"
                     checked={role === "admin"} onChange={() => setRole("admin")} />
              Admin
            </label>
            {role === "admin" && (
              <p className="muted">
                An admin can add venues and read every session. There is no way back from here:
                the only undo is removing their access, which takes them out of the study.
              </p>
            )}
          </fieldset>
        )}
        <button className="primary" disabled={busy || members === null || !email.trim()}>
          {busy ? "Adding..." : isOwner && role === "admin" ? "Add admin" : "Add field observer"}
        </button>
        {err && <p className="err">{err}</p>}
        {notice && <p className="ok">{notice}</p>}
        <p className="muted">
          Nothing is emailed from here. Share this link: <strong>{link}</strong>
        </p>
        <button type="button" onClick={() => void navigator.clipboard?.writeText(link).then(() => setNotice("Link copied."))}>
          Copy link
        </button>
      </form>

      <div className="card">
        <h3>On the team</h3>
        {loadErr && (
          <div className="stack">
            <p className="err">{loadErr}</p>
            <button type="button" onClick={() => void load()}>Try again</button>
          </div>
        )}
        {members === null ? (!loadErr && <p className="muted">Loading...</p>) : (
          <ul className="list">
            {members.map((m) => (
              <li key={m.id} className="row card" style={{ margin: 0 }}>
                <span>{m.display_name ?? m.email ?? "Unknown address"}</span>
                <small>
                  {m.display_name && m.email ? `${m.email} · ` : ""}
                  {label(m.role, m.is_owner)}
                  {m.revoked_at ? " · access removed" : ` · joined ${day(m.created_at)}`}
                </small>
                {isOwner && !m.is_owner && (
                  <button type="button" disabled={busy} onClick={() => void setAccess(m, m.revoked_at === null)}>
                    {m.revoked_at === null ? "Remove access" : "Give access back"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {pending.length > 0 && (
          <>
            <h3>Added, not signed in yet</h3>
            <ul className="list">
              {pending.map((i) => (
                <li key={i.email} className="row card" style={{ margin: 0 }}>
                  <span>{i.email}</span>
                  <small>{label(i.role)} · added {day(i.created_at)}</small>
                  <button type="button" onClick={() => void remove(i.email)}>Remove</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
